import { useState } from "react";
import { Edit, Archive, Trash } from "lucide-react";
import { Button } from "../components/ui/Button";
import ConfirmModal from "./ConfirmModal";
import { ConsultationTemplate } from "../types/consultation";
import { ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";

interface FormTemplatesTableProps {
  templates: ConsultationTemplate[];
  onEditTemplate: (id: string) => void;
  onArchiveTemplate: (id: string | number) => void;
  onDeleteTemplate?: (id: string) => void;
}

const FormTemplatesTable = ({
  templates,
  onEditTemplate,
  onArchiveTemplate,
  onDeleteTemplate,
}: FormTemplatesTableProps) => {
  const navigate = useNavigate();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);

  const handleDeleteClick = (uuid: string) => {
    setSelectedUuid(uuid);
    setConfirmOpen(true); // open the modal
  };

  const handleConfirmDelete = () => {
    if (selectedUuid && onDeleteTemplate) {
      onDeleteTemplate(selectedUuid);
    }
    setConfirmOpen(false);
    setSelectedUuid(null);
  };

  return (
    <>
      <div className="overflow-hidden rounded-xl bg-white border border-gray-200 shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-[#885ABB] border-b border-gray-200">
            <tr className="text-white">
              <th className="px-5 py-3 text-left font-semibold">#</th>
              <th className="px-5 py-3 text-left font-semibold">Title</th>
              <th className="px-5 py-3 text-left font-semibold">Description</th>
              <th className="px-5 py-3 text-left font-semibold">Frequency</th>
              <th className="px-5 py-3 text-left font-semibold">Status</th>
              <th className="px-5 py-3 text-left font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.map((t, i) => (
              <tr
                key={t.id}
                className={`${
                  i % 2 === 0 ? "bg-[#FCFCFD]" : "bg-white"
                } hover:bg-[#f6f4fb] transition-colors`}
              >
                <td className="px-5 py-4 text-[#402B69] font-medium">
                  {i + 1}
                </td>
                <td className="px-5 py-4 text-[#402B69] max-w-[200px] truncate">
                  {t.title}
                </td>
                <td className="px-5 py-4 max-w-xs text-gray-600 truncate">
                  {t.description}
                </td>
                <td className="px-5 py-4 text-gray-600">
                  {t.frequency || "-"}
                </td>
                <td className="px-5 py-4">
                  <span
                    className={`inline-flex items-center rounded-md px-2.5 py-0.5 text-xs font-medium ${
                      t.status === "published"
                        ? "bg-green-100 text-green-700"
                        : "bg-gray-200 text-gray-700"
                    }`}
                  >
                    {t.status || "draft"}
                  </span>
                </td>
                <td className="px-5 py-4 flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onEditTemplate(t.uuid)}
                    className="h-8 w-8 p-0 "
                  >
                    <Edit size={16} className="text-gray-500" />
                  </Button>

                  {/* <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onArchiveTemplate(t.uuid)}
                    className="h-8 w-8 p-0 hover:bg-[#f8e8e8]"
                  >
                    <Archive size={16} className="text-gray-500" />
                  </Button> */}

                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteClick(t.uuid)}
                    className="h-8 w-8 p-0  group"
                  >
                    <Trash
                      size={16}
                      className="text-gray-500 group-hover:text-red-500"
                    />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(`/consultationform/${t.uuid}`)}
                    className="h-8 w-8 p-0 group"
                  >
                    <ExternalLink
                      size={16}
                      className="text-gray-500 group-hover:text-blue-500"
                    />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmModal
        isOpen={confirmOpen}
        message="Are you sure you want to delete this form? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default FormTemplatesTable;
